import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormText,
  Row,
  Col,
} from 'reactstrap';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { IconButton } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { validateContractor } from '../Helpers/Config';

const selectStyles = {
  menuPortal: base => ({ ...base, zIndex: 999999 }),
  menu: base => ({ ...base, zIndex: 99999 }),
  control: base => ({ ...base, zIndex: 1 }),
};

const AddEditContractorModal = ({
  isModalOpen,
  setIsModalOpen,
  formik,
  isEdit = false,
  onRefresh,
  submitContractor,
  userRole,
  categories = [],
  availableProperties = [],  // Renamed from propertyList to match parent prop
}) => {
  const isCompanyUser = userRole === 'company';
  const isReadOnly = !isCompanyUser;

  const [localCategories, setLocalCategories] = useState([]);

  useEffect(() => {
    setLocalCategories(categories || []);
  }, [categories, isModalOpen]);

  const categoryOptions = localCategories.map((cat) => ({
    value: cat,
    label: cat.toUpperCase(),
  }));

  const selectedCategory =
    categoryOptions.find((opt) => opt.value === formik.values.category) || null;

  const propertyOptions = useMemo(
    () =>
      availableProperties.map(p => ({
        value: p._id,
        label: `${p.title || 'Unnamed'}, ${p.location || 'No location'}`,
      })),
    [availableProperties]
  );

  const addRepresentative = () => {
    const current = formik.values.representatives || [];
    formik.setFieldValue('representatives', [
      ...current,
      { name: '', phone: '', email: '' },
    ]);
  };

  const removeRepresentative = (index) => {
    const current = formik.values.representatives || [];
    formik.setFieldValue(
      'representatives',
      current.filter((_, i) => i !== index)
    );
  };

  const addPropertyAssignment = () => {
    const current = formik.values.properties || [];
    formik.setFieldValue('properties', [
      ...current,
      { property_id: '', building_name: '' },
    ]);
  };

  const removePropertyAssignment = (index) => {
    const current = formik.values.properties || [];
    formik.setFieldValue(
      'properties',
      current.filter((_, i) => i !== index)
    );
  };

  const getBuildingsForProperty = (propertyId) => {
    const prop = availableProperties.find(p => p._id === propertyId);
    return prop?.building
      ?.filter(b => b?.buildingName)
      ?.map(b => b.buildingName) ?? [];
  };

  const handlePropertyChange = (index, selected) => {
    const newId = selected?.value || '';
    formik.setFieldValue(`properties.${index}.property_id`, newId);

    const buildings = getBuildingsForProperty(newId);
    const autoBuilding = buildings.length === 1 ? buildings[0] : '';
    formik.setFieldValue(`properties.${index}.building_name`, autoBuilding);
  };

  const handleBuildingChange = (index, selected) => {
    formik.setFieldValue(`properties.${index}.building_name`, selected?.value || '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = await validateContractor(formik.values, isEdit);

    if (!isValid) {
      return;
    }

    try {
      await submitContractor(formik.values);
      setIsModalOpen(false);
      onRefresh();

      Swal.fire({
        icon: 'success',
        title: isEdit ? 'Contractor Updated' : 'Contractor Added',
        text: isEdit
          ? 'The contractor information has been successfully updated.'
          : 'The new contractor has been successfully added.',
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err.message || 'Could not save contractor. Please try again.',
      });
    }
  };

  return (
    <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(false)} size="lg">
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={() => setIsModalOpen(false)}>
          {isReadOnly
            ? 'View Contractor'
            : isEdit
              ? 'Edit Contractor'
              : 'Add New Contractor'}
        </ModalHeader>

        <ModalBody>
          <Row className="mb-3">
            <Col md={6}>
              <FormGroup>
                <Label for="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={formik.values.company_name || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.company_name && !!formik.errors.company_name
                  }
                  disabled={formik.isSubmitting || isReadOnly}
                  readOnly={isReadOnly}
                />
                {formik.touched.company_name && formik.errors.company_name && (
                  <FormText color="danger">
                    {formik.errors.company_name}
                  </FormText>
                )}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>Category *</Label>
                <CreatableSelect
                  options={categoryOptions}
                  value={selectedCategory}
                  onChange={(opt) =>
                    formik.setFieldValue('category', opt?.value || '')
                  }
                  onCreateOption={(inputValue) => {
                    const value = inputValue.trim();
                    if (!value) return;
                    const exists = localCategories.some(
                      (c) => c.toLowerCase() === value.toLowerCase()
                    );
                    if (!exists) {
                      setLocalCategories((prev) => [...prev, value]);
                    }
                    formik.setFieldValue('category', value);
                    formik.setFieldTouched('category', true);
                  }}
                  isClearable={isCompanyUser}
                  isSearchable={isCompanyUser}
                  isDisabled={formik.isSubmitting || isReadOnly}
                  formatCreateLabel={(inputValue) =>
                    `Create "${inputValue.toUpperCase()}"`
                  }
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {formik.touched.category && formik.errors.category && (
                  <FormText color="danger">
                    {formik.errors.category}
                  </FormText>
                )}
              </FormGroup>
            </Col>
          </Row>

          {/* Property Assignments */}
          <FormGroup className="mb-4">
            <Label>Assigned Properties</Label>
            {(formik.values.properties || []).map((assign, index) => {
              const buildings = getBuildingsForProperty(assign.property_id);
              const hasBuildings = buildings.length > 0;
              const hasMultipleBuildings = buildings.length > 1;

              const buildingOptions = buildings.map(b => ({ value: b, label: b }));
              const selectedPropertyOption = propertyOptions.find(o => o.value === assign.property_id) ?? null;
              const selectedBuildingOption = buildingOptions.find(o => o.value === assign.building_name) ?? null;

              return (
                <Row key={index} className="mb-2 align-items-end">
                  <Col md={hasBuildings ? 5 : isReadOnly ? 12 : 11}>
                    <Select
                      value={selectedPropertyOption}
                      onChange={(selected) => handlePropertyChange(index, selected)}
                      options={propertyOptions}
                      isClearable={!isReadOnly}
                      isDisabled={formik.isSubmitting || isReadOnly}
                      placeholder="Select property..."
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </Col>

                  {hasBuildings && (
                    <Col md={isReadOnly ? 12 : 5}>
                      <Select
                        value={selectedBuildingOption}
                        onChange={(selected) => handleBuildingChange(index, selected)}
                        options={buildingOptions}
                        isClearable={hasMultipleBuildings && !isReadOnly}
                        isDisabled={formik.isSubmitting || isReadOnly || !hasMultipleBuildings}
                        placeholder={hasMultipleBuildings ? "Select building..." : ""}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </Col>
                  )}

                  {!isReadOnly && (
                    <Col md={1}>
                      <IconButton
                        onClick={() => removePropertyAssignment(index)}
                        disabled={formik.isSubmitting}
                        color="error"
                        size="small"
                      >
                        <FeatherIcon icon="trash-2" size={18} />
                      </IconButton>
                    </Col>
                  )}
                </Row>
              );
            })}

            {!isReadOnly && (
              <Button
                type="button"
                color="link"
                onClick={addPropertyAssignment}
                disabled={formik.isSubmitting}
                className="p-0 mt-2"
              >
                <FeatherIcon icon="plus-circle" size={18} className="me-2" />
                Add Property Assignment
              </Button>
            )}

            {formik.touched.properties && formik.errors.properties && (
              <FormText color="danger">
                {formik.errors.properties}
              </FormText>
            )}
          </FormGroup>

          <FormGroup className="mb-3">
            <Label>Description</Label>
            <Input
              type="textarea"
              rows="3"
              name="description"
              value={formik.values.description || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isReadOnly}
              readOnly={isReadOnly}
            />
          </FormGroup>

          <FormGroup className="mb-4">
            <Label>Representatives *</Label>
            {(formik.values.representatives || []).map((rep, index) => (
              <Row key={index} className="mb-2 align-items-end">
                <Col md={4}>
                  <Input
                    placeholder="Name"
                    value={rep.name || ''}
                    onChange={(e) =>
                      formik.setFieldValue(
                        `representatives.${index}.name`,
                        e.target.value
                      )
                    }
                    disabled={formik.isSubmitting || isReadOnly}
                    readOnly={isReadOnly}
                  />
                </Col>
                <Col md={4}>
                  <Input
                    placeholder="Phone"
                    value={rep.phone || ''}
                    onChange={(e) =>
                      formik.setFieldValue(
                        `representatives.${index}.phone`,
                        e.target.value
                      )
                    }
                    invalid={
                      formik.touched.representatives?.[index]?.phone &&
                      !!formik.errors.representatives?.[index]?.phone
                    }
                    disabled={formik.isSubmitting || isReadOnly}
                    readOnly={isReadOnly}
                  />
                  {formik.touched.representatives?.[index]?.phone &&
                    formik.errors.representatives?.[index]?.phone && (
                      <FormText color="danger">
                        {formik.errors.representatives[index].phone}
                      </FormText>
                    )}
                </Col>
                <Col md={isReadOnly ? 4 : 3}>
                  <Input
                    placeholder="Email"
                    type="email"
                    value={rep.email || ''}
                    onChange={(e) =>
                      formik.setFieldValue(
                        `representatives.${index}.email`,
                        e.target.value
                      )
                    }
                    disabled={formik.isSubmitting || isReadOnly}
                    readOnly={isReadOnly}
                  />
                </Col>
                {isCompanyUser && (
                  <Col md={1}>
                    <IconButton
                      onClick={() => removeRepresentative(index)}
                      disabled={formik.isSubmitting}
                      color="error"
                      size="small"
                    >
                      <FeatherIcon icon="trash-2" size={18} />
                    </IconButton>
                  </Col>
                )}
              </Row>
            ))}

            {isCompanyUser && (
              <Button
                type="button"
                color="link"
                onClick={addRepresentative}
                disabled={formik.isSubmitting}
                className="p-0 mt-2"
              >
                <FeatherIcon icon="plus-circle" size={18} className="me-2" />
                Add Representative
              </Button>
            )}
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          {isCompanyUser && (
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={formik.isSubmitting}
            >
              {isEdit ? 'Update Contractor' : 'Save Contractor'}
            </Button>
          )}
          <Button
            type="button"
            color="secondary"
            onClick={() => setIsModalOpen(false)}
            disabled={formik.isSubmitting}
          >
            {isReadOnly ? 'Close' : 'Cancel'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export { AddEditContractorModal };